import React, { useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import {
  TAfindAllCampaigns,
  TAsetVisibility,
  TAupdateClassification,
  TAvoiceMessageCounts,
  TAupdateNote,
  TAdeleteNote,
  TAchangeDeleteStatus,
} from "../../../services/campaignsAPI"
import { selectUser } from "../../../redux/store/auth/authSlice"
import { Campaign } from "../../../types/CampaignDatas"
import { DataTableSortStatus } from "mantine-datatable"
import sortBy from "lodash/sortBy"
import {
  selectCampaignFilters,
  setCampaignFilters,
} from "../../../redux/store/filters/campaign/campaignFilterSlice"
import { columnConfig } from "./components/columns"
import StickyDataTable from "../../../components/StickyDataTable"
import CategoryModal from "./components/CategoryModal"
import ClassificationModal from "./components/ClassificationModal"
import CampaignNotesModal from "./components/NotesModal"
import ExcelUploadsModal from "../../../components/ExcelUploadsModal"
import Filters from "./components/Filters"
import { useNavigate } from "react-router-dom"
import { isAbortError, getErrorResponse, getErrorStatus } from "../../../types/error"

function AllCampaign() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const userEmail = useSelector(selectUser)?.replace(/"/g, "") || ""
  const abortControllerRef = useRef<AbortController | null>(null)
  const campaignFilters = useSelector(selectCampaignFilters)
  const [campaignData, setCampaignData] = useState([] as any[])
  const [filterState, setFilterState] = useState(campaignFilters)
  const [page, setPage] = useState(1)
  const PAGE_SIZES = [10, 20, 30, 50, 100, 500, 1000]
  const [pageSize, setPageSize] = useState(PAGE_SIZES[2])
  const [totalRecords, setTotalRecords] = useState(0)
  const [initialRecords, setInitialRecords] = useState(sortBy(campaignData, "id"))
  const [error, setError] = useState<string | null>(null)
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus>({
    columnAccessor: "id",
    direction: "asc",
  })
  const [sortByColumn, setSortByColumn] = useState("")
  const [sortOrder, setSortOrder] = useState(true)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const [isOpenCategoryModal, setIsOpenCategoryModal] = useState(false)
  const [isOpenClassificationModal, setIsOpenClassificationModal] = useState(false)
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false)
  const [isExcelUploadsModalOpen, setIsExcelUploadsModalOpen] = useState(false)
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("")
  const [selectedCampaignNotes, setSelectedCampaignNotes] = useState<any[]>([])
  const [classification, setClassification] = useState("")
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [selectedId, setSelectedId] = useState("")
  const [selectedClassification, setSelectedClassification] = useState([
    {
      main_category: {
        category: "",
        confidence: 0,
      },
      sub_categories: [
        {
          category: "",
          confidence: 0,
        },
      ],
    },
  ])
  const [copiedId, setCopiedId] = useState<string | null>(null)

  useEffect(() => {
    loadCampaigns()
  }, [sortByColumn, sortOrder, page, pageSize]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  useEffect(() => {
    dispatch(setPageTitle("All Campaigns"))
    setFilterState(campaignFilters)
    setClassification(campaignFilters.classification || "")
  }, [])

  useEffect(() => {
    const handleClick = () => {
      setIsDropdownOpen(false)
    }
    document.addEventListener("click", handleClick)
    return () => {
      document.removeEventListener("click", handleClick)
    }
  }, [])

  useEffect(() => {
    setSortStatus({
      columnAccessor: sortByColumn || "id",
      direction: sortOrder ? "desc" : "asc",
    })
  }, [sortByColumn, sortOrder])

  const loadCampaigns = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setLoading(true)
    let responseData: any[] = []
    try {
      const response = await TAfindAllCampaigns(
        filterState.created_at_start,
        filterState.created_at_end,
        filterState.country,
        filterState.platform,
        filterState.step,
        filterState.visibility,
        filterState.max_cost,
        filterState.gender,
        filterState.barter,
        filterState.deleted,
        filterState.min_follower,
        filterState.max_follower,
        filterState.min_age,
        filterState.max_age,
        sortByColumn,
        sortOrder,
        page,
        pageSize,
        filterState.campaignName,
        classification,
        abortControllerRef.current.signal,
      )

      if (response && Array.isArray(response.campaigns)) {
        const totalRecords = response.totalDocuments
        const data = response.campaigns.map((item: any) => {
          return {
            _id: item.details._id,
            ...item,
          }
        })

        setTotalRecords(totalRecords)
        setCampaignData(data)
        setInitialRecords(data)
        setError(null)
        responseData = data
      } else {
        setError("No data found")
      }
    } catch (error: unknown) {
      if (isAbortError(error)) {
        setLoading(false)
        return
      }

      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`No data found: ${getErrorResponse(error) || "Unknown error"}`)
    } finally {
      setLoading(false)
      if (responseData.length > 0) {
        await handleAllCounts(responseData)
      }
    }
  }

  useEffect(() => {
    setPage(1)
  }, [filterState, sortByColumn, sortOrder])

  const handleAllCounts = async (campaigns: any[]) => {
    try {
      const campaignIds = campaigns.map((campaign) => campaign._id)

      const [voiceResponse] = await Promise.all([TAvoiceMessageCounts(campaignIds)])

      const updatedCampaigns = campaigns.map((campaign) => {
        const campaignId = campaign._id

        const voiceMatched = voiceResponse.find((item: any) => item._id === campaignId)

        return {
          ...campaign,
          ...(voiceMatched && {
            voiceMessageInfo: voiceMatched.states,
          }),
        }
      })

      setInitialRecords(updatedCampaigns)
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating all counts, ${getErrorResponse(error) || "Unknown error"}`)
    }
  }

  const handleSorting = (columnAccessor: string) => {
    if (columnAccessor === sortByColumn) {
      setSortOrder((prevOrder) => !prevOrder)
    } else {
      setSortByColumn(columnAccessor)
      setSortOrder(true)
    }
  }

  const handleCampaignSelect = (selectedCampaign: any) => {
    setFilterState({ ...filterState, campaignName: selectedCampaign.name })
    dispatch(setCampaignFilters({ campaignName: selectedCampaign.name }))
  }

  const updateFilter = (key: keyof typeof filterState, value: any) => {
    const updated = { ...filterState, [key]: value }
    setFilterState(updated)
    dispatch(setCampaignFilters(updated))
    if (key === "classification") {
      setClassification(value || "")
    }
  }

  const handleToggleVisibility = async (_id: any, status: any) => {
    try {
      const res = await TAsetVisibility(_id, status)
      if (res) {
        const updatedCampaigns = initialRecords.map((campaign: any) => {
          if (campaign._id === _id) {
            return { ...campaign, visibility: status === "true" ? true : false }
          }
          return campaign
        })
        setInitialRecords([...updatedCampaigns])
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating visibility, ${getErrorResponse(error) || "Unknown error"}`)
    }
  }

  const handleToggleDeleteStatus = async (_id: any, status: any) => {
    try {
      const res = await TAchangeDeleteStatus(_id, status === "true")
      if (res) {
        const updatedCampaigns = initialRecords.map((campaign: any) => {
          if (campaign._id === _id) {
            return { ...campaign, deleted: status === "true" ? true : false }
          }
          return campaign
        })
        setInitialRecords([...updatedCampaigns])
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating delete status, ${getErrorResponse(error) || "Unknown error"}`)
    }
  }

  const handleClassificationChange = async (_id: string, classificationData: any) => {
    try {
      const res = await TAupdateClassification(_id, classificationData)
      if (res) {
        const updatedCampaigns = initialRecords.map((campaign: any) => {
          if (campaign._id === _id) {
            return { ...campaign, classification: classificationData }
          }
          return campaign
        })
        setInitialRecords([...updatedCampaigns])
      }
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating classification, ${getErrorResponse(error) || "Unknown error"}`)
    }
  }

  const handleUpdateNote = async (_id: string, note: string) => {
    try {
      const response = await TAupdateNote(_id, userEmail, note)
      const updatedCampaigns = initialRecords.map((campaign: any) => {
        if (campaign._id === _id) {
          return {
            ...campaign,
            notes: response?.notes || [
              ...campaign.notes,
              { note, author: userEmail, note_time: new Date() },
            ],
          }
        }
        return campaign
      })
      setInitialRecords([...updatedCampaigns])
      return response?.notes
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error updating note, ${getErrorResponse(error) || "Unknown error"}`)
    }
  }

  const handleDeleteNote = async (_id: string, index: number) => {
    try {
      await TAdeleteNote(_id, index)
      const updatedCampaigns = initialRecords.map((campaign: any) => {
        if (campaign._id === _id) {
          return {
            ...campaign,
            notes: campaign.notes.filter((note: any, noteIndex: number) => noteIndex !== index),
          }
        }
        return campaign
      })
      setInitialRecords([...updatedCampaigns])
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Error deleting note, ${getErrorResponse(error) || "Unknown error"}`)
    }
  }

  const onOpenNotesModal = (campaignId: string, notes: any[]) => {
    setSelectedCampaignId(campaignId)
    setSelectedCampaignNotes(notes)
    setIsNotesModalOpen(true)
  }

  const onOpenExcelUploadsModal = (campaignId: string) => {
    setSelectedCampaignId(campaignId)
    setIsExcelUploadsModalOpen(true)
  }

  const columns = columnConfig({
    handleToggleVisibility,
    handleToggleDeleteStatus,
    loading,
    handleSorting,
    setIsOpenClassificationModal,
    setSelectedId,
    setSelectedClassification,
    onOpenNotesModal,
    onOpenExcelUploadsModal,
    copiedId,
    setCopiedId,
  })

  return (
    <div className="panel">
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="flex md:items-center md:flex-row flex-col">
        <div className="flex flex-col md:flex-row p-5 gap-4">
          <div className="">
            <div className="form-check flex flex-row">
              <input
                type="text"
                className="form-input w-auto"
                name="campaignName"
                placeholder="Kampanya ismi ara"
                value={filterState.campaignName}
                onChange={(e) => updateFilter("campaignName", e.target.value)}
              />
              <label className="form-check-label p-2"></label>
            </div>
            {isDropdownOpen && initialRecords.length > 0 && (
              <div className="w-auto absolute bg-white border border-gray-300 rounded z-10">
                {initialRecords.slice(0, 4).map((match: Campaign) => (
                  <div
                    className="p-2 border-b border-gray-300 hover:bg-gray-100"
                    key={match._id}
                    onClick={() => handleCampaignSelect(match)}
                  >
                    {match.name}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div
            className="flex justify-center p-2 items-center border border-gray-300 rounded-xl cursor-pointer hover:bg-gray-100 md:w-1/10"
            onClick={() => setIsOpenCategoryModal(true)}
          >
            <span>{classification ? classification : "Sınıflandırma Filtresi"}</span>
          </div>
          <div
            className="flex w-1/10 bg-gray-100 items-center justify-center border border-gray-300 rounded-3xl p-2 cursor-pointer"
            onClick={() => setIsFilterOpen(true)}
          >
            Filtrele
          </div>
          <div
            className="flex w-1/10 bg-gray-100 items-center justify-center border border-gray-300 rounded-3xl p-2 cursor-pointer"
            onClick={() => loadCampaigns()}
          >
            Arama Yap
          </div>
        </div>
        <Filters
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={filterState}
          onFilterChange={(key, value) => updateFilter(key, value)}
          handleFetchData={loadCampaigns}
          handleClear={() => {
            const cleared = {
              platform: "",
              step: "",
              created_at: "",
              visibility: "",
              gender: "",
              country: "",
              barter: "",
              deleted: "false",
              max_follower: "",
              min_follower: "",
              max_age: "",
              min_age: "",
              max_cost: "",
              campaignName: "",
              classification: "",
            }
            setFilterState(cleared)
            dispatch(setCampaignFilters(cleared))
          }}
        />
      </div>
      <CategoryModal
        title="Kampanyaları Filtrele"
        isOpen={isOpenCategoryModal}
        onClose={() => setIsOpenCategoryModal(false)}
        onSelect={(category) => {
          updateFilter("classification", category)
        }}
        selectedValue={{ category: classification }}
      />
      <ClassificationModal
        title="Kampanya Sınıflandırma"
        isOpen={isOpenClassificationModal}
        onClose={() => setIsOpenClassificationModal(false)}
        onSelect={() => handleClassificationChange(selectedId, selectedClassification)}
        selectedClassification={selectedClassification}
        setSelectedClassification={setSelectedClassification}
      />
      <CampaignNotesModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        campaignId={selectedCampaignId}
        notes={selectedCampaignNotes}
        onAddNote={handleUpdateNote}
        onDeleteNote={handleDeleteNote}
      />

      <ExcelUploadsModal
        isOpen={isExcelUploadsModalOpen}
        onClose={() => setIsExcelUploadsModalOpen(false)}
        campaignId={selectedCampaignId}
      />

      <div className="datatables">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
          </div>
        ) : (
          <StickyDataTable
            records={initialRecords}
            columns={columns}
            totalRecords={totalRecords}
            recordsPerPage={pageSize}
            page={page}
            onPageChange={(p) => setPage(p)}
            recordsPerPageOptions={PAGE_SIZES}
            onRecordsPerPageChange={setPageSize}
            sortStatus={sortStatus}
            onSortStatusChange={(e) => handleSorting(e.columnAccessor)}
            minHeight={200}
            paginationText={({ from, to, totalRecords }) =>
              `Showing  ${from} to ${to} of ${totalRecords} entries`
            }
            highlightOnHover={true}
            className="whitespace-nowrap"
          />
        )}
      </div>
    </div>
  )
}

export default AllCampaign
